import { useTranslation } from "@sagot/i18n";
import { Skeleton, StatCard, type StatDelta } from "@sagot/ui";
import { useBuildFormatters } from "../shared/format";
import type { ActionStats } from "../shared/types";

const PERCENT = 100;

function runsDelta(stats: ActionStats, label: (value: string) => string): StatDelta | undefined {
  if (stats.runs_previous === 0) return undefined;
  const change = Math.round(((stats.runs - stats.runs_previous) / stats.runs_previous) * PERCENT);
  const value = `${change >= 0 ? "+" : "−"}${String(Math.abs(change))}%`;
  return { direction: change >= 0 ? "up" : "down", label: label(value), positive: change >= 0 };
}

/** KPI row (mockup `section.grid` of 4 stat cards). */
export function ActionKpis({ stats, agentName }: { stats: ActionStats | undefined; agentName: string }) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  if (!stats) {
    return (
      <section className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-hidden="true">
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-28 rounded-card" />)}
      </section>
    );
  }
  const failedNote = stats.failed === 0
    ? t("build.actions.kpi.failed", { count: 0 })
    : stats.top_failure
      ? t("build.actions.kpi.mostly", { count: stats.failed, reason: stats.top_failure })
      : t("build.actions.kpi.failed", { count: stats.failed });
  return (
    <section className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-label={t("build.actions.title")}>
      <StatCard label={t("build.actions.kpi.active")} icon="zap" value={`${String(stats.active)}/${String(stats.total)}`} footnote={agentName} />
      <StatCard
        label={t("build.actions.kpi.runs")}
        icon="activity"
        value={fmt.number(stats.runs)}
        delta={runsDelta(stats, (value) => t("build.actions.kpi.runsDelta", { value }))}
      />
      <StatCard
        label={t("build.actions.kpi.successRate")}
        icon="check-circle"
        value={stats.success_rate === null ? "—" : fmt.percent(stats.success_rate, 1)}
        footnote={stats.runs === 0 ? t("build.actions.kpi.none") : failedNote}
      />
      <StatCard
        label={t("build.actions.kpi.median")}
        icon="clock"
        value={fmt.duration(stats.median_ms)}
        footnote={stats.p95_ms === null ? undefined : t("build.actions.kpi.p95", { value: fmt.duration(stats.p95_ms) })}
      />
    </section>
  );
}
